/**
 * Configuration de TanStack Query (`L03-07`).
 *
 * Deux décisions structurantes :
 *
 * * **Ne jamais réessayer un refus métier.** Un `409` de conflit, un `422` de validation
 *   ou un `401` de session expirée ne deviendront pas vrais en insistant — et réessayer
 *   une écriture en conflit trois fois est le meilleur moyen d'écraser la mauvaise ligne.
 *   Seules les pannes passagères sont rejouées (`STO-08`).
 * * **Clés de cache nommées par domaine.** Une invalidation croisée — enregistrer une
 *   course doit rafraîchir les agrégats et les heatmaps — se déclare avec le préfixe du
 *   domaine, sans énumérer les requêtes concernées.
 */

import { QueryClient } from '@tanstack/react-query';

import { ApiError, NetworkError } from '@/lib/api';

/** Préfixes de clés, un par domaine. Toujours passer par eux, jamais par un littéral. */
export const keys = {
  health: () => ['health'] as const,
  session: () => ['session'] as const,

  body: {
    all: () => ['body'] as const,
    weight: () => ['body', 'weight'] as const,
    measurements: () => ['body', 'measurements'] as const,
    /** FC de repos, VFC et forme du jour (`docs/coach-course.md`, **C3**). */
    morning: () => ['body', 'morning'] as const,
    /** Les deux mesures sur trente jours (M-09) — sous `morning`, que toute écriture invalide. */
    morningTrend: () => ['body', 'morning', 'trend'] as const,
    /** Les douleurs, leurs épisodes et leur réponse graduée (`docs/douleurs.md`). */
    pain: () => ['body', 'pain'] as const,
    /** Ce qui va avec chaque douleur (`docs/patterns-douleur.md`) — sous `pain`, que toute écriture invalide. */
    painPatterns: () => ['body', 'pain', 'patterns'] as const,
  },
  activity: {
    all: () => ['activity'] as const,
    /** Les natations et leurs semaines (`docs/natation-activite.md`). */
    swims: () => ['activity', 'swims'] as const,
    /** Vélo, marche, randonnée — et la liste des sports qu'on peut saisir (M-16). */
    others: () => ['activity', 'others'] as const,
    overview: () => ['activity', 'overview'] as const,
    progress: () => ['activity', 'progress'] as const,
    runs: () => ['activity', 'runs'] as const,
    run: (id: number) => ['activity', 'runs', id] as const,
    /** La dernière course et ses paliers — ce que `/activite/course` ouvre par défaut. */
    latestRun: () => ['activity', 'runs', 'latest'] as const,
    runProgress: () => ['activity', 'runs', 'progress'] as const,
    /** Charge et corrélations — `docs/coach-course.md` §6. */
    runTrends: () => ['activity', 'runs', 'trends'] as const,
    runSplits: (id: number) => ['activity', 'runs', id, 'splits'] as const,
    /** L'analyse d'une course importée — courbe, tracé, constats, zones. Une requête à
     *  part, parce qu'elle se relit depuis le `.fit` rangé et que la page doit s'afficher
     *  sans l'attendre. */
    runAnalysis: (id: number) => ['activity', 'runs', id, 'analysis'] as const,
    /** Effort perçu, météo et forme du matin d'une course. */
    runConditions: (id: number) => ['activity', 'runs', id, 'conditions'] as const,
    /** La sortie contre la séance prévue ce jour-là (CA-04). */
    runAdherence: (id: number) => ['activity', 'runs', id, 'adherence'] as const,
    workouts: () => ['activity', 'workouts'] as const,
    workout: (id: number) => ['activity', 'workouts', id] as const,
    exercises: () => ['activity', 'exercises'] as const,
    types: () => ['activity', 'types'] as const,
    muscleGroups: () => ['activity', 'muscle-groups'] as const,
    circuits: () => ['activity', 'circuits'] as const,
    circuitExercises: (query = '') => ['activity', 'circuits', 'exercises', query] as const,
    loads: () => ['activity', 'loads'] as const,
    load: (name: string) => ['activity', 'loads', name] as const,
  },
  hydration: {
    all: () => ['hydration'] as const,
    day: (day: string) => ['hydration', 'day', day] as const,
  },
  supplements: {
    all: () => ['supplements'] as const,
    schedule: () => ['supplements', 'schedule'] as const,
    today: () => ['supplements', 'today'] as const,
    units: () => ['supplements', 'units'] as const,
  },
  nutrition: {
    all: () => ['nutrition'] as const,
    meals: (day: string) => ['nutrition', 'meals', day] as const,
    history: (range: string) => ['nutrition', 'history', range] as const,
    favorites: () => ['nutrition', 'favorites'] as const,
    catalog: (range: string) => ['nutrition', 'catalog', range] as const,
    food: (id: string) => ['nutrition', 'catalog', 'food', id] as const,
  },
  aggregates: {
    all: () => ['aggregates'] as const,
    dashboard: () => ['aggregates', 'dashboard'] as const,
    /** La charge tous sports, les alertes ouvertes et la nuit (`docs/accueil-et-charge.md`).
     *  Sous `aggregates` à dessein : toute écriture les invalide déjà (`CROSS_CUTTING`). */
    training: () => ['aggregates', 'training'] as const,
    alerts: () => ['aggregates', 'alerts'] as const,
    night: () => ['aggregates', 'night'] as const,
    metrics: () => ['aggregates', 'metrics'] as const,
    series: (metric: string, range: string, subject?: string) =>
      ['aggregates', 'series', metric, range, subject ?? ''] as const,
  },
  heatmap: {
    all: () => ['heatmap'] as const,
    tracks: () => ['heatmap', 'tracks'] as const,
    grid: (tracks: string[], from: string, to: string) =>
      ['heatmap', 'grid', tracks.join(','), from, to] as const,
    /** Grilles de l'écran : toutes les pistes actives, plage par défaut du serveur. */
    screen: () => ['heatmap', 'grid', 'all'] as const,
    day: (track: string, day: string) => ['heatmap', 'day', track, day] as const,
  },
  medical: {
    all: () => ['medical'] as const,
  },
  races: {
    all: () => ['races'] as const,
    /** Les courses visées et leurs conflits (`docs/plan-course.md`). */
    list: () => ['races', 'list'] as const,
    plan: (raceId: string) => ['races', 'plan', raceId] as const,
  },
  planning: {
    all: () => ['planning'] as const,
    month: (month: string) => ['planning', 'month', month] as const,
    adherence: () => ['planning', 'adherence'] as const,
    /** Ce que le coach a changé et qui attend une réponse (`docs/agenda-replanification.md`). */
    changes: () => ['planning', 'changes'] as const,
    agenda: () => ['planning', 'agenda'] as const,
    /** Adresse d'abonnement (`PLAN-05`) — elle vient d'un réglage serveur, elle ne
     *  change pas en cours de session. */
    subscription: () => ['planning', 'subscription'] as const,
    /** Le glossaire de nage. Sous `planning` : un libellé renommé change ce que le mois
     *  affiche, et l'invalidation du domaine doit l'emporter avec lui. */
    swimCodes: () => ['planning', 'swim-codes'] as const,
    swimParse: (program: string) => ['planning', 'swim-parse', program] as const,
    /** L'aperçu d'une séance de course (`docs/planifier-une-seance.md`, D2). */
    runParse: (program: string) => ['planning', 'run-parse', program] as const,
    runTemplates: (duration: number | null) => ['planning', 'run-templates', duration] as const,
    runCoach: (day: string) => ['planning', 'run-coach', day] as const,
    /** Les garde-fous d'une séance en cours de saisie (`docs/planifier-une-seance.md`, D9). */
    check: (body: object) => ['planning', 'check', body] as const,
  },
  assistant: {
    all: () => ['assistant'] as const,
    /** Les fils de discussion (`IA-13`). Ils vivent sur le serveur depuis le lot L18. */
    threads: () => ['assistant', 'threads'] as const,
    thread: (id: string) => ['assistant', 'threads', id] as const,
    /** Le fil posé sous la grille du planning — un seul, ouvert à la première question. */
    planningThread: () => ['assistant', 'threads', 'planning'] as const,
    /** Le carnet de mémoire (`IA-11`). Le fil de conversation, lui, n'est jamais mis en
     *  cache : il n'existe pas côté serveur. */
    memory: () => ['assistant', 'memory'] as const,
    /** Le profil (« Ce que je suis »). Il vit dans `settings.csv`, d'où l'invalidation
     *  croisée avec `settings` à l'écriture — les deux se partagent un jeton de fichier. */
    profile: () => ['assistant', 'profile'] as const,
  },
  brief: {
    all: () => ['brief'] as const,
    /** La lecture du jour. Une seule par journée, et le serveur décide laquelle. */
    today: () => ['brief', 'today'] as const,
  },
  goals: {
    all: () => ['goals'] as const,
    active: () => ['goals', 'active'] as const,
    /** Historique des bilans hebdomadaires (`IA-08`). */
    weekly: () => ['goals', 'weekly'] as const,
  },
  settings: {
    all: () => ['settings'] as const,
  },
  ai: {
    all: () => ['ai'] as const,
    /** État de l'assistance (`IA-07`) — lu une fois, il ne change pas en cours de session. */
    status: () => ['ai', 'status'] as const,
  },
  evening: {
    all: () => ['evening'] as const,
    /** Le point du soir : dû ou non, et son contenu (`docs/notifications-v3.md`). */
    flow: () => ['evening', 'flow'] as const,
  },
  morning: {
    all: () => ['morning'] as const,
    /** Le parcours du matin : dû ou non, et où reprendre. */
    flow: () => ['morning', 'flow'] as const,
  },
  coach: {
    all: () => ['coach'] as const,
    next: () => ['coach', 'next'] as const,
  },
  /** Sécurité et données (`docs/exploitation-securite.md`). */
  account: {
    all: () => ['account'] as const,
    view: () => ['account', 'view'] as const,
    passkeys: () => ['account', 'passkeys'] as const,
  },
  notifications: {
    all: () => ['notifications'] as const,
    /** État du push, appareils abonnés et créneaux, en une requête (`NOT-01`, `NOT-03`). */
    view: () => ['notifications', 'view'] as const,
  },
} as const;

/**
 * Domaines dont l'écriture doit rafraîchir les vues transverses.
 *
 * Toute source d'une piste d'assiduité invalide les grilles (`HEAT-33`) et le tableau de
 * bord (`AGG-01`) : sans cela, cocher un supplément laisserait la heatmap mentir jusqu'à
 * la prochaine navigation.
 */
export const CROSS_CUTTING = [keys.aggregates.all(), keys.heatmap.all()];

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof NetworkError) return failureCount < 2;
  if (error instanceof ApiError) return error.isTransient && failureCount < 2;
  return false;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        // Les données changent parce que l'utilisateur les saisit, pas tout seules :
        // une fenêtre courte évite de relire Nextcloud à chaque navigation.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Une écriture n'est jamais rejouée automatiquement : un conflit (`STO-05`) doit
        // remonter à l'utilisateur, pas être forcé.
        retry: false,
      },
    },
  });
}
